import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingWelcome from './OnboardingWelcome';
import OnboardingContact from './OnboardingContact';
import OnboardingAnimals from './OnboardingAnimals';
import OnboardingPreferences from './OnboardingPreferences';
import OnboardingReview from './OnboardingReview';
import LauraChat from '../../../components/StoreClub/LauraChat';
import { useStoreClubAuth } from '../../../contexts/StoreClubAuthContext';

export interface OnboardingData {
  name: string;
  email: string;
  phone: string;
  password: string;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  animals: AnimalData[];
  subscribe_to_reminders: boolean;
  subscribe_to_weather_alerts: boolean;
}

export interface AnimalData {
  animal_type: string;
  name?: string;
  breed?: string;
  count?: number;
  weight_lbs?: number;
  activity_level?: string;
  life_stage?: string;
  notes?: string;
}

type OnboardingStep = 'welcome' | 'contact' | 'animals' | 'preferences' | 'review';

const STEPS: OnboardingStep[] = ['welcome', 'contact', 'animals', 'preferences', 'review'];

const OnboardingFlow: React.FC = () => {
  const navigate = useNavigate();
  const { register, setMemberFromTokens } = useStoreClubAuth();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome');
  const [data, setData] = useState<OnboardingData>({
    name: '',
    email: '',
    phone: '',
    password: '',
    street_address: '',
    city: '',
    state: 'WA',
    zip_code: '',
    animals: [],
    subscribe_to_reminders: true,
    subscribe_to_weather_alerts: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lauraMessage, setLauraMessage] = useState("Hi there! I'm Laura, your friendly guide to the Store Club. Ready to get started?");

  const stepIndex = STEPS.indexOf(currentStep);
  const progress = ((stepIndex) / (STEPS.length - 1)) * 100;

  const updateData = (updates: Partial<OnboardingData>) => {
    setData(prev => ({ ...prev, ...updates }));
  };

  const nextStep = () => {
    const nextIndex = stepIndex + 1;
    if (nextIndex < STEPS.length) {
      setCurrentStep(STEPS[nextIndex]);
      updateLauraMessage(STEPS[nextIndex]);
      requestAnimationFrame(() => window.scrollTo(0, 0));
    }
  };

  const prevStep = () => {
    const prevIndex = stepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex]);
      updateLauraMessage(STEPS[prevIndex]);
      requestAnimationFrame(() => window.scrollTo(0, 0));
    }
  };

  const updateLauraMessage = (step: OnboardingStep) => {
    const messages: Record<OnboardingStep, string> = {
      welcome: "Hi there! I'm Laura, your friendly guide to the Store Club. Ready to get started?",
      contact: "Let's start with the basics! I'll need your contact info so we can keep you updated on your orders.",
      animals: "Now for the fun part - tell me about your furry, feathery, or woolly family members!",
      preferences: "Almost done! Let me know how you'd like to stay in touch.",
      review: "Looking good! Take a moment to review everything, and we'll get you all set up!",
    };
    setLauraMessage(messages[step]);
  };

  const handleSubmit = async (password: string) => {
    setIsSubmitting(true);
    setError('');

    try {
      // Include password directly in the registration data to avoid race condition
      const registrationData = { ...data, password };
      console.log('[Onboarding] Submitting registration:', {
        ...registrationData,
        password: password ? `***${password.length} chars` : 'MISSING',
        animals: registrationData.animals?.length || 0,
      });

      const response = await register(registrationData);
      console.log('[Onboarding] Registration successful:', response);
      setMemberFromTokens(response.access, response.refresh, response.member);
      navigate('/club/dashboard');
    } catch (err: any) {
      console.error('[Onboarding] Registration failed:', err);
      setError(err.message || 'Registration failed. Please try again.');
      setLauraMessage("Oops! Something went wrong. Let me help you fix that.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'welcome':
        return <OnboardingWelcome onNext={nextStep} />;
      case 'contact':
        return <OnboardingContact data={data} updateData={updateData} onNext={nextStep} onBack={prevStep} />;
      case 'animals':
        return <OnboardingAnimals data={data} updateData={updateData} onNext={nextStep} onBack={prevStep} />;
      case 'preferences':
        return <OnboardingPreferences data={data} updateData={updateData} onNext={nextStep} onBack={prevStep} />;
      case 'review':
        return (
          <OnboardingReview
            data={data}
            updateData={updateData}
            onBack={prevStep}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            error={error}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <img src="/rbf-logo.png" alt="Red Bridge Farm" style={styles.logo} />
        <h1 style={styles.title}>Store Club</h1>
      </div>

      {currentStep !== 'welcome' && (
        <div style={styles.progressContainer}>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${progress}%` }} />
          </div>
          <div style={styles.stepIndicators}>
            {STEPS.slice(1).map((step, idx) => (
              <div
                key={step}
                style={{
                  ...styles.stepDot,
                  backgroundColor: idx <= stepIndex - 1 ? '#508590' : '#e0e0e0',
                }}
              />
            ))}
          </div>
        </div>
      )}

      <LauraChat message={lauraMessage} />

      <div style={styles.content}>
        {renderStep()}
      </div>

      <div style={styles.footer}>
        <p style={styles.footerText}>Red Bridge Farm Feed Co. &bull; Kettle Falls, WA</p>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8f9fa',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    padding: '1.5rem',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e0e0e0',
  },
  logo: {
    width: '50px',
    height: 'auto',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#E73C3E',
    margin: 0,
    fontFamily: "'Open Sans', sans-serif",
  },
  progressContainer: {
    padding: '1rem 1.5rem',
    backgroundColor: '#fff',
  },
  progressBar: {
    height: '4px',
    backgroundColor: '#e0e0e0',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#508590',
    transition: 'width 0.3s ease',
  },
  stepIndicators: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '0.5rem',
    padding: '0 0.5rem',
  },
  stepDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    transition: 'background-color 0.3s ease',
  },
  content: {
    flex: 1,
    padding: '1.5rem',
    maxWidth: '600px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  footer: {
    padding: '1rem',
    textAlign: 'center',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#fff',
  },
  footerText: {
    margin: 0,
    color: '#666',
    fontSize: '0.875rem',
  },
};

export default OnboardingFlow;
